import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { JournalEntry, JournalEntryResponse } from "../types";

interface UseJournalEntryResult {
  entry: JournalEntry | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch a single journal entry by ID.
 */
export function useJournalEntry(id: string | undefined): UseJournalEntryResult {
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!id) {
      setEntry(null);
      setIsLoading(false);
      return;
    }

    const fetchEntry = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<JournalEntryResponse>(
          `/api/journal-entries/${id}`,
        );
        setEntry(data.entry);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setEntry(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntry();
  }, [id, fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    entry,
    isLoading,
    error,
    refetch,
  };
}
